import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import capitalize from 'lodash-es/capitalize';
import type { Dayjs } from 'dayjs';
import { StoresInventoryStatus } from '../stores/model';
import { Day } from '../stores/model/Day';
import type { OrderAddress } from '../baskets/model';
import type { Store } from '../stores/model';
import type {
    StoreHolidayHour,
    StoreHolidayHours,
    StoreStandardHours,
} from '../stores/model/StoreHours';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(weekday);

const DATE_FORMAT = 'YYYY-MM-DD';
const DAY_LONG_FORMAT = 'dddd';
const TIME_FORMAT = 'HH:mm';

interface FormatAmPmOptions {
    infix?: string;
}

const getWeekDatesByDay = (): Record<Day, Dayjs> => {
    const now = dayjs();
    return {
        [Day.MONDAY]: now.day(1),
        [Day.TUESDAY]: now.day(2),
        [Day.WEDNESDAY]: now.day(3),
        [Day.THURSDAY]: now.day(4),
        [Day.FRIDAY]: now.day(5),
        [Day.SATURDAY]: now.day(6),
        [Day.SUNDAY]: now.day(7),
    };
};

const formatAmPm = (time: string, options?: FormatAmPmOptions): string => {
    try {
        const splitTime = time.split(':');
        const hours = parseInt(splitTime[0], 10);
        const minutes = parseInt(splitTime[1], 10);

        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return '';
        }

        const formattedMinutes = `${minutes}`.padStart(2, '0');
        if (hours < 12) {
            const newHours = hours === 0 ? 12 : hours;
            return `${newHours}:${formattedMinutes}${options?.infix ?? ''}am`;
        }

        const newHours = hours > 12 ? hours - 12 : hours;
        return `${newHours}:${formattedMinutes}${options?.infix ?? ''}pm`;
    } catch {
        return '';
    }
};

const toDay = (date: Dayjs): Day => date.format(DAY_LONG_FORMAT).toUpperCase() as Day;

export const format12Hour = (startTime: string, endTime: string): string => {
    const startHour = formatAmPm(startTime);
    const endHour = formatAmPm(endTime);

    return `${startHour} - ${endHour}`;
};

export const formatStoreHours = (standardHours: StoreStandardHours, day: Day): string => {
    const hours = standardHours[day];

    return format12Hour(hours.startTime, hours.endTime);
};

export const isStoreOpen = (store: Store): boolean => {
    const { c_storeHoursData, c_timeZone } = store;

    if (!c_storeHoursData) {
        return false;
    }

    if (isTodaysStoreClosedDate(store)) {
        return false;
    }

    const today = dayjs().tz(c_timeZone);
    const todayDay = toDay(today);
    let todayStoreHours = c_storeHoursData.standardHours[todayDay];

    const todaysHoliday = getTodayStoreHoliday(store);
    if (todaysHoliday) {
        todayStoreHours = todaysHoliday;
    }

    if (!todayStoreHours) {
        return false;
    }

    const currentTime = today.format(TIME_FORMAT);
    const { startTime, endTime } = todayStoreHours;

    if (startTime && endTime) {
        return currentTime >= startTime.padStart(5, '0') && currentTime <= endTime.padStart(5, '0');
    }

    return false;
};

const isStoreClosedDate = ({ c_storeHoursData, c_timeZone }: Store, date: Dayjs): boolean => {
    if (!c_storeHoursData) {
        return false;
    }

    const todayDateString = date.tz(c_timeZone).format(DATE_FORMAT);

    return c_storeHoursData.closedDates.some((closedDate) => todayDateString === closedDate);
};

const isTodaysStoreClosedDate = (store: Store): boolean => {
    const today = dayjs().tz(store.c_timeZone);

    return isStoreClosedDate(store, today);
};

const isTomorrowStoreClosedDate = (store: Store): boolean => {
    const tomorrow = dayjs().tz(store.c_timeZone).add(1, 'day');

    return isStoreClosedDate(store, tomorrow);
};

export const isClosedDay = (day: Day, closedDates: string[]): boolean => {
    const dayDateString = getWeekDatesByDay()[day].format(DATE_FORMAT);

    return closedDates.some((closedDate) => dayDateString === closedDate);
};

export const getHoliday = (
    day: Day,
    holidayHours: StoreHolidayHours,
): StoreHolidayHour | undefined => {
    const dayDateString = getWeekDatesByDay()[day].format(DATE_FORMAT);

    return holidayHours.find((holiday) => holiday.date === dayDateString);
};

export const getTodayStoreHoliday = (store?: Store): StoreHolidayHour | undefined => {
    if (!store?.c_storeHoursData?.holidayHours) {
        return undefined;
    }

    const today = dayjs().tz(store.c_timeZone);

    const todayDay = toDay(today);

    return getHoliday(todayDay, store.c_storeHoursData.holidayHours);
};

const getTomorrowStoreHoliday = (store?: Store): StoreHolidayHour | undefined => {
    if (!store?.c_storeHoursData?.holidayHours) {
        return undefined;
    }

    const tomorrow = dayjs().tz(store.c_timeZone).add(1, 'day');

    const tomorrowDay = toDay(tomorrow);

    return getHoliday(tomorrowDay, store.c_storeHoursData.holidayHours);
};

export const getTomorrowStoreOpeningTime = (store: Store, options?: FormatAmPmOptions): string => {
    const { c_storeHoursData, c_timeZone } = store;

    if (!c_storeHoursData) {
        return '';
    }

    if (isTodaysStoreClosedDate(store)) {
        return '';
    }

    if (isTomorrowStoreClosedDate(store)) {
        return '';
    }

    const tomorrow = dayjs().tz(c_timeZone).add(1, 'day');

    const tomorrowDay = toDay(tomorrow);

    let hours = c_storeHoursData.standardHours[tomorrowDay];

    const tomorrowsHoliday = getTomorrowStoreHoliday(store);
    if (tomorrowsHoliday) {
        hours = tomorrowsHoliday;
    }

    return formatAmPm(hours?.startTime, options);
};

export const getTodaysStoreOpeningTime = (store: Store, options?: FormatAmPmOptions): string => {
    const { c_storeHoursData, c_timeZone } = store;

    if (!c_storeHoursData) {
        return '';
    }

    if (isTodaysStoreClosedDate(store)) {
        return '';
    }

    const today = dayjs().tz(c_timeZone);

    const todayDay = toDay(today);

    const currentTime = today.format(TIME_FORMAT);

    let hours = c_storeHoursData.standardHours[todayDay];

    const todaysHoliday = getTodayStoreHoliday(store);
    if (todaysHoliday) {
        hours = todaysHoliday;
    }

    const startTime = hours?.startTime;

    if (startTime && startTime.padStart(5, '0') > currentTime) {
        return formatAmPm(startTime, options);
    }

    return '';
};

export const getTodaysStoreClosingTime = (store: Store, options?: FormatAmPmOptions): string => {
    const { c_storeHoursData, c_timeZone } = store;

    if (!c_storeHoursData) {
        return '';
    }

    const today = dayjs().tz(c_timeZone);

    const todayDay = toDay(today);

    let hours = c_storeHoursData.standardHours[todayDay];

    const todaysHoliday = getTodayStoreHoliday(store);
    if (todaysHoliday) {
        hours = todaysHoliday;
    }

    return formatAmPm(hours?.endTime, options);
};

export const getDistanceDescription = (distance: number, unit: string): string =>
    `(${distance} ${capitalize(unit)})`;

export const isAvailableStatus = (status: StoresInventoryStatus): boolean =>
    status === StoresInventoryStatus.PRODUCT_AVAILABLE ||
    status === StoresInventoryStatus.PRODUCT_AVAILABLE_IN_ANOTHER_QUANTITY ||
    status === StoresInventoryStatus.NOT_LOADED;

export const isNotAvailableStatus = (status: StoresInventoryStatus): boolean =>
    !isAvailableStatus(status);

export const isNotEligibleStatus = (status: StoresInventoryStatus): boolean =>
    status === StoresInventoryStatus.DISABLED ||
    status === StoresInventoryStatus.PRODUCT_NOT_ELIGIBLE;

export const toOrderAddress = (store?: Store | null): OrderAddress | undefined => {
    if (!store) {
        return undefined;
    }

    const { address1, address2, city, stateCode, countryCode, postalCode, latitude, longitude } =
        store;

    return {
        address1,
        address2,
        city,
        stateCode,
        countryCode,
        postalCode,
        c_latitude: latitude,
        c_longitude: longitude,
    } as OrderAddress;
};
